/* Pitch detection with the McLeod Pitch Method (FFT-based normalised square difference). */
import { getFFT } from './fft';
import { nextPow2, type Samples, work } from './math';

export interface PitchOptions {
  minF?: number;
  maxF?: number;
  /** Key-maximum threshold relative to the highest peak. */
  k?: number;
  minRms?: number;
  /** Octave-up guard margin; 0 disables it. */
  octaveCheck?: number;
}

export interface PitchResult {
  /** Hz, 0 when the window is unvoiced. */
  freq: number;
  clarity: number;
  rms: number;
}

/** `buf` is one analysis window (~40 ms). */
export function detectPitch(buf: Samples, sr: number, opts: PitchOptions = {}): PitchResult {
  const minF = opts.minF || 60;
  const maxF = opts.maxF || 1400;
  const K = opts.k || 0.93;
  const W = buf.length;
  let mean = 0;
  for (let i = 0; i < W; i++) mean += buf[i];
  mean /= W;
  const x = work('x', W);
  let ss = 0;
  for (let i = 0; i < W; i++) {
    const v = buf[i] - mean;
    x[i] = v;
    ss += v * v;
  }
  const rms = Math.sqrt(ss / W);
  if (rms < (opts.minRms || 1e-4)) return { freq: 0, clarity: 0, rms };
  const N = nextPow2(2 * W);
  const re = work('re', N);
  const im = work('im', N);
  for (let i = 0; i < W; i++) {
    re[i] = x[i];
    im[i] = 0;
  }
  for (let i = W; i < N; i++) {
    re[i] = 0;
    im[i] = 0;
  }
  const fft = getFFT(N);
  fft(re, im, false);
  for (let i = 0; i < N; i++) {
    re[i] = re[i] * re[i] + im[i] * im[i];
    im[i] = 0;
  }
  fft(re, im, true); // re[tau] = autocorrelation r(tau)
  const maxLag = Math.min(W - 3, Math.ceil(sr / minF));
  const minLag = Math.max(2, Math.floor(sr / maxF));
  const nsdf = work('nsdf', W);
  let m = 2 * ss;
  for (let tau = 0; tau <= maxLag + 1; tau++) {
    nsdf[tau] = m > 1e-12 ? (2 * re[tau]) / m : 0;
    m -= x[W - 1 - tau] * x[W - 1 - tau] + x[tau] * x[tau];
  }
  // key maxima: highest point of each positive lobe after the first negative zero crossing
  const peaks: number[] = [];
  let inLobe = false;
  let bestIdx = -1;
  let bestVal = -Infinity;
  const accept = (i: number) => i > minLag && nsdf[i] >= nsdf[i - 1] && nsdf[i] >= nsdf[i + 1];
  for (let tau = 1; tau <= maxLag; tau++) {
    const prev = nsdf[tau - 1];
    const cur = nsdf[tau];
    if (prev <= 0 && cur > 0) {
      inLobe = true;
      bestIdx = -1;
      bestVal = -Infinity;
    }
    if (inLobe) {
      if (cur > bestVal && tau >= minLag) {
        bestVal = cur;
        bestIdx = tau;
      }
      if (prev > 0 && cur <= 0) {
        if (bestIdx > 0 && accept(bestIdx)) peaks.push(bestIdx);
        inLobe = false;
      }
    }
  }
  if (inLobe && bestIdx > 0 && bestIdx < maxLag && accept(bestIdx)) peaks.push(bestIdx);
  if (!peaks.length) return { freq: 0, clarity: 0, rms };
  let maxVal = -Infinity;
  for (const p of peaks) if (nsdf[p] > maxVal) maxVal = nsdf[p];
  const thr = K * maxVal;
  let p = peaks[0] as number;
  for (const q of peaks) {
    if (nsdf[q] >= thr) {
      p = q;
      break;
    }
  }
  const oc = opts.octaveCheck == null ? 0.02 : opts.octaveCheck;
  if (oc > 0) {
    // octave-up guard: a lobe near 2p that is clearly stronger means p was half the real period
    for (const q of peaks) {
      if (q > p && Math.abs(q - 2 * p) <= Math.max(2, 0.03 * p) && nsdf[q] > nsdf[p] + oc) {
        p = q;
        break;
      }
    }
  }
  const a = nsdf[p - 1];
  const b = nsdf[p];
  const c = nsdf[p + 1];
  const den = a - 2 * b + c;
  let shift = 0;
  if (Math.abs(den) > 1e-12) shift = (0.5 * (a - c)) / den;
  if (shift > 0.5 || shift < -0.5) shift = 0;
  const period = p + shift;
  const clarity = Math.min(1, b - 0.25 * (a - c) * shift);
  return { freq: sr / period, clarity, rms };
}
